import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemSettings from "./SystemSettings";
import { getSystemSettings, updateSystemSettings } from "../../api/methods";
import Loading from "../../components/shared/Loading";
import { showToast } from "../../helpers/showToast";
import { EDITABLE_FIELDS, SETTING_GROUPS } from "./SystemSettings.config";

const SystemSettingsContainer = ({ tab }) => {
  const { t } = useTranslation();

  const [loader, setLoader] = useState(false);
  const [settings, setSettings] = useState([]);
  // Only the values the user has touched, keyed by setting key.
  const [edits, setEdits] = useState({});
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reason, setReason] = useState("");

  const showError = (err) => {
    showToast({
      toastOptions: { type: "error" },
      data: {
        variant: "error",
        text: err?.errorMessage || err?.message || t("common.general_error"),
      },
    });
  };

  const handleGetSettings = () => {
    setLoader(true);
    getSystemSettings()
      .then((response) => {
        setSettings(Array.isArray(response) ? response : []);
        setEdits({});
      })
      .catch((err) => {
        console.log(err);
        showError(err);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    handleGetSettings();
  }, []);

  const onFieldChange = (key, field, fieldValue) => {
    setEdits((prevEdits) => ({
      ...prevEdits,
      [key]: { ...prevEdits[key], [field]: fieldValue },
    }));
  };

  // One item per setting that actually differs from the server, carrying only
  // the fields that changed. key is always sent; it identifies the setting.
  const changedItems = useMemo(
    () =>
      settings.reduce((items, setting) => {
        const edit = edits[setting.key];
        if (!edit) {
          return items;
        }

        const item = { key: setting.key };
        EDITABLE_FIELDS.forEach((field) => {
          if (
            edit[field] !== undefined &&
            edit[field] !== (setting[field] ?? "")
          ) {
            item[field] = edit[field];
          }
        });

        if (Object.keys(item).length > 1) {
          items.push(item);
        }
        return items;
      }, []),
    [settings, edits]
  );

  // Settings arrive as a flat list, one row per key. Split them into the
  // Dashboard / Taxpayer sections defined in SETTING_GROUPS, then fall back to
  // the backend category for any key the config does not cover.
  const groups = useMemo(() => {
    const configuredKeys = [];

    const configured = SETTING_GROUPS.map((group) => {
      const items = group.keys
        .map((key) => settings.find((setting) => setting.key === key))
        .filter(Boolean);

      items.forEach((item) => configuredKeys.push(item.key));

      return {
        id: group.id,
        title: t(`system_settings.groups.${group.id}`),
        items,
      };
    }).filter((group) => group.items.length > 0);

    const rest = [];
    settings
      .filter((setting) => !configuredKeys.includes(setting.key))
      .forEach((setting) => {
        const id = setting.category || "uncategorized";
        const group = rest.find((g) => g.id === id);
        if (group) {
          group.items.push(setting);
        } else {
          rest.push({
            id,
            title: setting.category
              ? t(`system_settings.categories.${setting.category}`, setting.category)
              : t("system_settings.uncategorized"),
            items: [setting],
          });
        }
      });

    return [...configured, ...rest];
  }, [settings, t]);

  const onDiscard = () => {
    setEdits({});
  };

  const onSaveClick = () => {
    setReason("");
    setShowReasonModal(true);
  };

  const onCloseReasonModal = () => {
    setShowReasonModal(false);
  };

  const onConfirmSave = () => {
    setShowReasonModal(false);
    setLoader(true);

    updateSystemSettings({ reason: reason.trim(), items: changedItems })
      .then(() => {
        showToast({
          toastOptions: { type: "success" },
          data: {
            variant: "success",
            text: t("system_settings.saveSuccess"),
          },
        });
        // Re-read the list so the new values, updatedAt and updatedBy all come
        // from the server; handleGetSettings also clears the pending edits.
        handleGetSettings();
      })
      .catch((err) => {
        console.log(err);
        showError(err);
        setLoader(false);
      });
  };

  return (
    <React.Fragment>
      {loader && <Loading />}
      <SystemSettings
        tab={tab}
        groups={groups}
        edits={edits}
        onFieldChange={onFieldChange}
        changedCount={changedItems.length}
        onSaveClick={onSaveClick}
        onDiscard={onDiscard}
        showReasonModal={showReasonModal}
        reason={reason}
        onReasonChange={setReason}
        onCloseReasonModal={onCloseReasonModal}
        onConfirmSave={onConfirmSave}
      />
    </React.Fragment>
  );
};

export default React.memo(SystemSettingsContainer);
